import type { inferInput, inferOutput } from "@trpc/tanstack-react-query";
import type { FormEvent } from "react";
import { Link, createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import ArrowLeftIcon from "lucide-react/dist/esm/icons/arrow-left";
import ArchiveIcon from "lucide-react/dist/esm/icons/archive";
import EditIcon from "lucide-react/dist/esm/icons/edit";
import PlusIcon from "lucide-react/dist/esm/icons/plus";
import RotateCwIcon from "lucide-react/dist/esm/icons/rotate-cw";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import { Button } from "@quake/shared-ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { Separator } from "@quake/shared-ui/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@quake/shared-ui/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@quake/shared-ui/components/ui/tabs";
import { toast } from "@quake/shared-ui/lib/sonner";
import {
  getListMemberKey,
  LIST_MEMBER_LIMIT,
  LIST_MEMBER_MUTATION_LIMIT,
  listRecordTypes,
  type ListRecordType,
} from "@quake/isomorphic/lists";
import { MultiRecordPicker, type RecordOption } from "~/components/multi-record-picker";
import { checkPermission } from "~/store/usePermissionStore";
import { trpc } from "~/utils/trpc";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import {
  ConfirmListActionDialog,
  ListFormDialog,
  ListStateBadge,
  RenewListDialog,
  VisibilityBadge,
  canManageList,
  entityLabels,
  formatExpiryDate,
  formatDateTime,
  isListOwner,
  isListExpired,
  isListLocked,
  type ListFormPayload,
} from "./-components/lists-ui";
import { ListMembersSpreadsheet } from "./-components/list-members-spreadsheet";

export { ListMemberItem } from "./-components/list-members-spreadsheet";

declare module "@tanstack/history" {
  interface HistoryState {
    listCandidateDialogOrigin?: string;
  }
}

const CandidateDetailsDialog = lazy(() =>
  import("../candidates/-pages/candidate-details-dialog").then((module) => ({
    default: module.CandidateDetailsDialog,
  })),
);

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/lists/$listId")({
  validateSearch: z.object({ candidate: z.string().uuid().optional().catch(undefined) }),
  component: ListDetailRoute,
});

type ListMembersResponse = inferOutput<typeof trpc.lists.listMembers>;
type ListMember = ListMembersResponse["members"][number];
type ListMembersPage = { offset: number; response: ListMembersResponse };
type ListMemberMoveDestination = inferInput<typeof trpc.lists.moveMember>["destination"];
type RowMoveDestination = Exclude<ListMemberMoveDestination, { type: "position" }>;

const MEMBERS_PAGE_SIZE = 50;

function ListDetailRoute() {
  const { datasetSlug, listId } = Route.useParams();
  const listQuery = useQuery(trpc.lists.getById.queryOptions({ id: listId }));
  useBreadCrumbs([
    { title: "Lists", link: `/${datasetSlug}/data/lists` },
    { title: listQuery.data?.name ?? "List", link: `/${datasetSlug}/data/lists/${listId}` },
  ]);

  return <ListDetailPage datasetSlug={datasetSlug} listId={listId} />;
}

function ListDetailPage({ datasetSlug, listId }: { datasetSlug: string; listId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const { candidate: selectedCandidateId } = Route.useSearch();
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState<RecordOption[]>([]);
  const [membersOffset, setMembersOffset] = useState(0);
  const [memberPages, setMemberPages] = useState<ListMembersPage[]>([]);

  const listTypesQuery = useQuery(trpc.lists.listTypes.queryOptions({ includeArchived: true }));
  const sessionQuery = useQuery(trpc.auth.getSession.queryOptions());
  const listQuery = useQuery(trpc.lists.getById.queryOptions({ id: listId }));
  const membersQuery = useQuery(
    trpc.lists.listMembers.queryOptions({ listId, limit: MEMBERS_PAGE_SIZE, offset: membersOffset }),
  );
  const activityQuery = useQuery(trpc.lists.listActivity.queryOptions({ listId }));

  const list = listQuery.data;
  useEffect(() => {
    if (!membersQuery.data) return;
    setMemberPages((pages) =>
      [
        ...pages.filter((page) => page.offset !== membersOffset),
        { offset: membersOffset, response: membersQuery.data },
      ].sort((left, right) => left.offset - right.offset),
    );
  }, [membersOffset, membersQuery.data]);

  const members = useMemo(() => memberPages.flatMap((page) => page.response.members), [memberPages]);
  const recordSummaries = useMemo<ListMembersResponse["recordSummaries"]>(
    () => Object.assign({}, ...memberPages.map((page) => page.response.recordSummaries)),
    [memberPages],
  );
  const candidateDialogItems = useMemo(
    () =>
      members.flatMap((member) => {
        if (member.record_type !== "candidate") return [];
        const summary = recordSummaries[getListMemberKey({ recordType: "candidate", recordId: member.record_id })];
        return [
          {
            id: member.record_id,
            name: summary?.label || "Unnamed candidate",
            subtitle: summary?.subtitle ?? undefined,
            status: summary?.status ?? undefined,
          },
        ];
      }),
    [members, recordSummaries],
  );
  const selectCandidate = (id: string, replace = false) => {
    void navigate({
      to: "/$datasetSlug/data/lists/$listId",
      params: { datasetSlug, listId },
      search: (previous) => ({ ...previous, candidate: id }),
      state: (previous) => ({
        ...previous,
        listCandidateDialogOrigin: replace ? previous.listCandidateDialogOrigin : router.state.location.pathname,
      }),
      replace,
      resetScroll: false,
    });
  };
  const closeCandidate = () => {
    if (router.history.location.state.listCandidateDialogOrigin === router.state.location.pathname) {
      router.history.back();
      return;
    }
    void navigate({
      to: "/$datasetSlug/data/lists/$listId",
      params: { datasetSlug, listId },
      search: (previous) => ({ ...previous, candidate: undefined }),
      replace: true,
      resetScroll: false,
    });
  };
  const inaccessibleCount = useMemo(
    () => memberPages.reduce((total, page) => total + page.response.inaccessibleCount, 0),
    [memberPages],
  );
  const memberCount = memberPages[0]?.response.total;
  const lastMemberPage = memberPages[memberPages.length - 1];
  const hasMoreMembers = lastMemberPage?.response.hasMore ?? false;
  const locked = list ? isListLocked(list) : true;
  const atMemberCapacity = memberCount !== undefined && memberCount >= LIST_MEMBER_LIMIT;

  const existingRecordKeys = useMemo(
    () =>
      new Set(
        members.map((member) => getListMemberKey({ recordType: member.record_type, recordId: member.record_id })),
      ),
    [members],
  );

  const refreshDetail = async () => {
    setMemberPages([]);
    setMembersOffset(0);
    await Promise.all([
      queryClient.invalidateQueries(trpc.lists.list.queryFilter()),
      queryClient.invalidateQueries(trpc.lists.getById.queryFilter({ id: listId })),
      queryClient.invalidateQueries(trpc.lists.listMembers.queryFilter({ listId })),
      queryClient.invalidateQueries(trpc.lists.listActivity.queryFilter({ listId })),
    ]);
  };

  const updateList = useMutation(
    trpc.lists.update.mutationOptions({
      onSuccess: async () => {
        await refreshDetail();
        toast.success("List updated.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const renewList = useMutation(
    trpc.lists.renew.mutationOptions({
      onSuccess: async () => {
        await refreshDetail();
        toast.success("List renewed.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const archiveList = useMutation(
    trpc.lists.archive.mutationOptions({
      onSuccess: async () => {
        await refreshDetail();
        toast.success("List archived.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const hardDeleteList = useMutation(
    trpc.lists.hardDelete.mutationOptions({
      onSuccess: async () => {
        toast.success("List deleted.");
        await navigate({ to: "/$datasetSlug/data/lists", params: { datasetSlug } });
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const removeMembers = useMutation(
    trpc.lists.removeMembers.mutationOptions({
      onSuccess: async () => {
        await refreshDetail();
        toast.success("Member removed.");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const addMembers = useMutation(
    trpc.lists.addMembers.mutationOptions({
      onSuccess: async () => {
        const addedCount = selectedRecords.length;
        setSelectedRecords([]);
        setAddMembersOpen(false);
        await refreshDetail();
        toast.success(`${addedCount.toLocaleString()} record${addedCount === 1 ? "" : "s"} added.`);
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const moveListMember = useMutation(
    trpc.lists.moveMember.mutationOptions({
      onSuccess: async () => {
        await refreshDetail();
        toast.success("Member moved.");
      },
      onError: async (error) => {
        await refreshDetail();
        toast.error(error.message);
      },
    }),
  );

  const canUpdate = checkPermission("lists", "update");
  const canDelete = checkPermission("lists", "delete");
  const canManageMembers = checkPermission("lists", "manage_members");
  const isListManager =
    !!list &&
    canManageList(list, sessionQuery.data?.user?.user_dataset_id, sessionQuery.data?.user?.role === "Administrator");
  const canUpdateList = canUpdate && isListManager;
  const canDeleteList = canDelete && isListManager;
  const canManageListMembers = canManageMembers && isListManager;
  const canManageSharing = !!list && isListOwner(list, sessionQuery.data?.user?.user_dataset_id);
  const canMoveMembers = canManageListMembers && !locked && members.length > 0 && !moveListMember.isPending;

  const submitEdit = async (payload: ListFormPayload) => {
    await updateList.mutateAsync({
      id: listId,
      listTypeId: payload.listTypeId,
      name: payload.name,
      description: payload.description,
      context: payload.context,
      mode: payload.mode,
      remoteId: payload.remoteId,
      expiresAt: payload.expiresAt,
      sharing: payload.sharing,
    });
  };

  const requestMemberMove = async (member: ListMember, destination: RowMoveDestination) => {
    if (!list) return;
    await moveListMember.mutateAsync({
      listId,
      member: { recordType: member.record_type, recordId: member.record_id },
      destination,
      expectedVersion: list.version,
    });
  };

  if (listQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading list...</div>;
  }

  if (!list) {
    return <div className="p-6 text-sm text-muted-foreground">List unavailable.</div>;
  }

  return (
    <div className="m-5 flex min-h-[calc(100vh-6rem)] flex-col gap-4">
      <div className="flex flex-col gap-4 rounded-md border bg-background p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <Button asChild variant="ghost" size="sm" className="mb-2 -ml-2 h-8 text-muted-foreground">
              <Link to="/$datasetSlug/data/lists" params={{ datasetSlug }}>
                <ArrowLeftIcon className="mr-2 size-4" />
                Lists
              </Link>
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="wrap-break-word text-2xl font-bold tracking-tight">{list.name}</h1>
              <ListStateBadge list={list} />
              <VisibilityBadge visibility={list.sharing_mode} />
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span>{members.length.toLocaleString()} visible members loaded</span>
              <span>
                {memberCount === undefined
                  ? "Loading member capacity..."
                  : `${memberCount.toLocaleString()} of ${LIST_MEMBER_LIMIT.toLocaleString()} members`}
              </span>
              <span>Version {list.version}</span>
              <span>Expires {formatExpiryDate(list.expires_at)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canUpdateList ? (
              <Button type="button" variant="outline" onClick={() => setRenewOpen(true)}>
                <RotateCwIcon className="mr-2 size-4" />
                Renew
              </Button>
            ) : null}
            {canUpdateList ? (
              <Button type="button" variant="outline" onClick={() => setEditOpen(true)} disabled={locked}>
                <EditIcon className="mr-2 size-4" />
                Edit
              </Button>
            ) : null}
            {canUpdateList ? (
              <Button
                type="button"
                variant="outline"
                disabled={list.status === "archived"}
                onClick={() => setArchiveConfirmOpen(true)}
              >
                <ArchiveIcon className="mr-2 size-4" />
                Archive
              </Button>
            ) : null}
            {canDeleteList ? (
              <Button type="button" variant="destructive" onClick={() => setDeleteConfirmOpen(true)}>
                <Trash2Icon className="mr-2 size-4" />
                Delete
              </Button>
            ) : null}
          </div>
        </div>
        {locked ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
            {list.status === "archived"
              ? "Archived lists are read-only."
              : "Expired lists are read-only until renewed."}
          </div>
        ) : null}
      </div>

      <Tabs defaultValue="members" className="min-h-0 flex-1">
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="min-h-0">
          <div className="rounded-md border bg-background">
            <div className="flex flex-col gap-3 border-b p-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="text-sm font-medium">Members</div>
                <div className="text-xs text-muted-foreground">
                  {inaccessibleCount > 0
                    ? `${inaccessibleCount} loaded member${inaccessibleCount === 1 ? "" : "s"} hidden by visibility rules.`
                    : "Ordered mixed records for this list."}
                </div>
              </div>
              {canManageListMembers ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={locked || atMemberCapacity}
                    onClick={() => setAddMembersOpen(true)}
                  >
                    <PlusIcon className="mr-2 size-4" />
                    Add records
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="flex flex-col gap-3 p-3">
              {membersQuery.isLoading && memberPages.length === 0 ? (
                <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                  Loading members...
                </div>
              ) : (
                <ListMembersSpreadsheet
                  listId={listId}
                  meta={list.meta}
                  members={members}
                  recordSummaries={recordSummaries}
                  datasetSlug={datasetSlug}
                  moveEnabled={canMoveMembers}
                  canConfigure={canUpdateList}
                  removeEnabled={canManageListMembers}
                  locked={locked}
                  onOpenCandidate={(id, trigger) => {
                    returnFocusRef.current = trigger;
                    selectCandidate(id);
                  }}
                  onMoveMember={requestMemberMove}
                  onRemoveMember={(member) =>
                    removeMembers.mutate({
                      listId,
                      recordRefs: [{ recordType: member.record_type, recordId: member.record_id }],
                    })
                  }
                />
              )}
              {hasMoreMembers && lastMemberPage ? (
                <Button
                  type="button"
                  variant="outline"
                  className="cursor-pointer self-center"
                  disabled={membersQuery.isFetching}
                  onClick={() => setMembersOffset(lastMemberPage.offset + MEMBERS_PAGE_SIZE)}
                >
                  {membersQuery.isFetching ? "Loading..." : `Load more (${members.length.toLocaleString()} loaded)`}
                </Button>
              ) : null}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="rounded-md border bg-background">
            <Table useDynamicHeight={false}>
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activityQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} className="h-32 text-center text-sm text-muted-foreground">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2Icon className="size-4 animate-spin" />
                        Loading activity...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (activityQuery.data ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="h-32 text-center text-sm text-muted-foreground">
                      No activity yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  (activityQuery.data ?? []).map((activity) => (
                    <TableRow key={activity.id}>
                      <TableCell className="font-medium">{activity.action}</TableCell>
                      <TableCell>{formatDateTime(activity.created_at)}</TableCell>
                      <TableCell className="max-w-xl truncate text-xs text-muted-foreground">
                        {JSON.stringify(activity.data ?? {})}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="settings">
          <div className="grid gap-4 rounded-md border bg-background p-4 lg:grid-cols-[1fr_20rem]">
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold">List settings</h2>
                <p className="text-sm text-muted-foreground">Core list metadata.</p>
              </div>
              <Separator />
              <dl className="grid gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-muted-foreground">Mode</dt>
                  <dd className="font-medium capitalize">{list.mode}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Visibility</dt>
                  <dd className="font-medium">
                    {list.sharing_mode === "dataset"
                      ? "Shared with dataset"
                      : list.sharing_mode === "users"
                        ? "Shared with people"
                        : "Only me"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd className="font-medium">{formatDateTime(list.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Updated</dt>
                  <dd className="font-medium">{formatDateTime(list.updated_at)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Expires</dt>
                  <dd className="font-medium">{formatExpiryDate(list.expires_at)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="font-medium">
                    {list.status === "archived" ? "Archived" : isListExpired(list) ? "Expired" : "Active"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Remote ID</dt>
                  <dd className="font-medium">{list.remote_id || "-"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Deleted</dt>
                  <dd className="font-medium">{formatDateTime(list.deleted_at)}</dd>
                </div>
              </dl>
              <div className="rounded-md border bg-muted/20 p-3">
                <div className="text-sm font-medium">Description</div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
                  {list.description || "No description added."}
                </p>
              </div>
              {list.context ? (
                <div className="rounded-md border bg-muted/20 p-3">
                  <div className="text-sm font-medium">Context</div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{list.context}</p>
                </div>
              ) : null}
            </div>
            <div className="space-y-2 rounded-md border bg-muted/20 p-3">
              <div className="text-sm font-medium">Actions</div>
              {canUpdateList ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  disabled={locked}
                  onClick={() => setEditOpen(true)}
                >
                  <EditIcon className="mr-2 size-4" />
                  Edit metadata
                </Button>
              ) : null}
              {canUpdateList ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setRenewOpen(true)}
                >
                  <RotateCwIcon className="mr-2 size-4" />
                  Renew expiry
                </Button>
              ) : null}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {selectedCandidateId ? (
        <Suspense
          fallback={
            <div
              role="status"
              className="fixed right-4 top-4 z-40 rounded-lg border bg-background px-4 py-3 text-sm text-muted-foreground shadow-lg"
            >
              Loading candidate details…
            </div>
          }
        >
          <CandidateDetailsDialog
            candidateId={selectedCandidateId}
            datasetSlug={datasetSlug}
            items={candidateDialogItems}
            onSelect={(id) => selectCandidate(id, true)}
            onClose={closeCandidate}
            returnFocusRef={returnFocusRef}
          />
        </Suspense>
      ) : null}

      <ListFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        listTypes={listTypesQuery.data ?? []}
        initialList={list}
        isSubmitting={updateList.isPending}
        canManageSharing={canManageSharing}
        onSubmit={submitEdit}
      />
      <RenewListDialog
        open={renewOpen}
        onOpenChange={setRenewOpen}
        list={list}
        isSubmitting={renewList.isPending}
        onRenew={async (expiresAt) => {
          await renewList.mutateAsync({ id: listId, expiresAt });
        }}
      />
      <ConfirmListActionDialog
        open={archiveConfirmOpen}
        onOpenChange={setArchiveConfirmOpen}
        title="Archive list"
        description={`Archive ${list.name}? Archived lists are read-only until restored by an admin.`}
        confirmLabel="Archive"
        isSubmitting={archiveList.isPending}
        onConfirm={async () => {
          await archiveList.mutateAsync({ id: list.id });
        }}
      />
      <ConfirmListActionDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete list"
        description={`Permanently delete ${list.name}? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        isSubmitting={hardDeleteList.isPending}
        onConfirm={async () => {
          await hardDeleteList.mutateAsync({ id: list.id, confirm: true });
        }}
      />
      <AddListMembersDialog
        open={addMembersOpen}
        onOpenChange={(open) => {
          setAddMembersOpen(open);
          if (!open) setSelectedRecords([]);
        }}
        existingRecordKeys={existingRecordKeys}
        memberCount={memberCount}
        selectedRecords={selectedRecords}
        onSelectedRecordsChange={(records) => {
          if (records.length > LIST_MEMBER_MUTATION_LIMIT) {
            toast.error(
              `A list membership request can include at most ${LIST_MEMBER_MUTATION_LIMIT.toLocaleString()} records.`,
            );
            return;
          }
          setSelectedRecords(records);
        }}
        isSubmitting={addMembers.isPending}
        onSubmit={async () => {
          await addMembers.mutateAsync({
            listId,
            recordRefs: selectedRecords.map((record) => ({
              recordType: record.source as ListRecordType,
              recordId: record.id,
            })),
          });
        }}
      />
    </div>
  );
}

function AddListMembersDialog({
  open,
  onOpenChange,
  existingRecordKeys,
  memberCount,
  selectedRecords,
  onSelectedRecordsChange,
  isSubmitting,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingRecordKeys: Set<string>;
  memberCount: number | undefined;
  selectedRecords: RecordOption[];
  onSelectedRecordsChange: (records: RecordOption[]) => void;
  isSubmitting?: boolean;
  onSubmit: () => Promise<void>;
}) {
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedRecords.length === 0) {
      toast.error("Select at least one record.");
      return;
    }
    await onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add records</DialogTitle>
          <DialogDescription>
            Search candidates, jobs, clients, and contacts to add to this list. Lists can contain up to{" "}
            {LIST_MEMBER_LIMIT.toLocaleString()} members.
          </DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <MultiRecordPicker
            modal
            value={selectedRecords}
            onValueChange={onSelectedRecordsChange}
            models={listRecordTypes.map((name) => ({ name }))}
            filter={(record) =>
              listRecordTypes.includes(record.source as ListRecordType) &&
              !existingRecordKeys.has(
                getListMemberKey({ recordType: record.source as ListRecordType, recordId: record.id }),
              )
            }
            placeholder="Search records"
            disabled={isSubmitting}
            compact
          />
          <div className="text-xs text-muted-foreground">
            {selectedRecords.length > 0
              ? `${selectedRecords.length.toLocaleString()} selected`
              : memberCount === undefined
                ? "Loading member capacity..."
                : `${memberCount.toLocaleString()} of ${LIST_MEMBER_LIMIT.toLocaleString()} members used. Existing list members are hidden from search results.`}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || selectedRecords.length === 0}>
              {isSubmitting ? "Adding..." : "Add records"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
